import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  User,
  Shield,
  Bell,
  CreditCard,
  Store,
  Key,
  Globe,
  Mail,
  Lock,
  Camera,
  Check,
  X,
  AlertCircle,
  Loader2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { DashboardLayout } from '../components/layouts/DashboardLayout';
import { ProfileSection } from '../components/settings/ProfileSection';
import { SecuritySection } from '../components/settings/SecuritySection';
import { SubscriptionSection } from '../components/settings/SubscriptionSection';
import { ContactSection } from '../components/settings/ContactSection';
import { SellerSection } from '../components/settings/SellerSection';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

type SettingsTab = 'profile' | 'security' | 'notifications' | 'subscription' | 'seller' | 'contact';

interface UserProfile {
  user_id: string;
  full_name: string | null;
  username: string | null;
  avatar_url: string | null;
  bio: string | null;
  phone: string | null;
  country: string | null;
  city: string | null;
  company: string | null;
  job_title: string | null;
  years_trading: number | null;
  trading_style: string | null;
  timezone: string;
  language: string;
}

interface NotificationSettings {
  user_id: string;
  email_notifications: boolean;
  bot_alerts: boolean;
  trade_notifications: boolean;
  profit_alerts: boolean;
  loss_alerts: boolean;
  daily_summary: boolean;
  marketing_emails: boolean;
}

interface SubscriptionData {
  id: string;
  plan_type: 'free' | 'basic' | 'pro' | 'enterprise';
  status: 'active' | 'cancelled' | 'expired' | 'trial';
  started_at: string;
  expires_at: string | null;
  auto_renew: boolean;
}

interface SellerProfile {
  user_id: string;
  seller_name: string | null;
  seller_bio: string | null;
  verified: boolean;
  total_sales: number;
  rating: number;
  bank_account_name: string | null;
  bank_account_number: string | null;
  bank_name: string | null;
  bank_country: string | null;
  paypal_email: string | null;
  payout_method: 'bank' | 'paypal' | 'crypto' | null;
}

const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [isSettingsCollapsed, setIsSettingsCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [sellerProfile, setSellerProfile] = useState<SellerProfile | null>(null);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (user?.id) {
      loadSettings();
    }
  }, [user?.id, activeTab]);

  const loadSettings = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      if (activeTab === 'profile') {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') throw error;

        if (!data) {
          const newProfile: UserProfile = {
            user_id: user.id,
            full_name: user.email?.split('@')[0] || null,
            username: null,
            avatar_url: null,
            bio: null,
            phone: null,
            country: null,
            city: null,
            company: null,
            job_title: null,
            years_trading: null,
            trading_style: null,
            timezone: 'UTC',
            language: 'en'
          };
          setProfile(newProfile);
        } else {
          setProfile(data);
        }
      }

      if (activeTab === 'notifications') {
        const { data, error } = await supabase
          .from('user_notification_settings')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') throw error;

        if (!data) {
          const defaultSettings: NotificationSettings = {
            user_id: user.id,
            email_notifications: true,
            bot_alerts: true,
            trade_notifications: true,
            profit_alerts: true,
            loss_alerts: true,
            daily_summary: false,
            marketing_emails: false
          };
          setNotificationSettings(defaultSettings);
        } else {
          setNotificationSettings(data);
        }
      }

      if (activeTab === 'subscription') {
        const { data, error } = await supabase
          .from('user_subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') throw error;

        if (!data) {
          setSubscription({
            id: '',
            plan_type: 'free',
            status: 'active',
            started_at: new Date().toISOString(),
            expires_at: null,
            auto_renew: false
          });
        } else {
          setSubscription(data);
        }
      }

      if (activeTab === 'seller') {
        const { data, error } = await supabase
          .from('seller_profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') throw error;

        if (!data) {
          const newSeller: SellerProfile = {
            user_id: user.id,
            seller_name: null,
            seller_bio: null,
            verified: false,
            total_sales: 0,
            rating: 0,
            bank_account_name: null,
            bank_account_number: null,
            bank_name: null,
            bank_country: null,
            paypal_email: null,
            payout_method: null
          };
          setSellerProfile(newSeller);
        } else {
          setSellerProfile(data);
        }
      }
    } catch (error: any) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    if (!user?.id || !profile) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .upsert({
          user_id: user.id,
          full_name: profile.full_name,
          username: profile.username,
          avatar_url: profile.avatar_url,
          bio: profile.bio,
          phone: profile.phone,
          country: profile.country,
          city: profile.city,
          company: profile.company,
          job_title: profile.job_title,
          years_trading: profile.years_trading,
          trading_style: profile.trading_style,
          timezone: profile.timezone,
          language: profile.language
        });

      if (error) throw error;

      toast.success('Profile updated successfully');
    } catch (error: any) {
      console.error('Error saving profile:', error);
      toast.error(error.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const saveNotificationSettings = async () => {
    if (!user?.id || !notificationSettings) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('user_notification_settings')
        .upsert({
          user_id: user.id,
          ...notificationSettings
        });

      if (error) throw error;

      toast.success('Notification settings updated');
    } catch (error: any) {
      console.error('Error saving notification settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const saveSellerProfile = async () => {
    if (!user?.id || !sellerProfile) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('seller_profiles')
        .upsert({
          user_id: user.id,
          seller_name: sellerProfile.seller_name,
          seller_bio: sellerProfile.seller_bio,
          bank_account_name: sellerProfile.bank_account_name,
          bank_account_number: sellerProfile.bank_account_number,
          bank_name: sellerProfile.bank_name,
          bank_country: sellerProfile.bank_country,
          paypal_email: sellerProfile.paypal_email,
          payout_method: sellerProfile.payout_method
        });

      if (error) throw error;

      toast.success('Seller profile updated');
    } catch (error: any) {
      console.error('Error saving seller profile:', error);
      toast.error('Failed to save seller profile');
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      toast.success('Password changed successfully');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      console.error('Error changing password:', error);
      toast.error(error.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'profile' as SettingsTab, label: 'Profile', icon: User },
    { id: 'security' as SettingsTab, label: 'Security', icon: Shield },
    { id: 'notifications' as SettingsTab, label: 'Notifications', icon: Bell },
    { id: 'subscription' as SettingsTab, label: 'Subscription', icon: CreditCard },
    { id: 'seller' as SettingsTab, label: 'Seller Account', icon: Store },
    { id: 'contact' as SettingsTab, label: 'Contact Us', icon: Mail }
  ];

  const getPlanBadgeColor = (plan: string) => {
    switch (plan) {
      case 'free': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      case 'basic': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'pro': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'enterprise': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <DashboardLayout>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="w-full space-y-6"
      >
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">Manage your account settings and preferences</p>
            </div>

            <div className="flex flex-col md:flex-row gap-6">
              <div className={`flex-shrink-0 transition-all duration-300 ${isSettingsCollapsed ? 'md:w-16' : 'md:w-64'}`}>
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    {!isSettingsCollapsed && (
                      <span className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase">
                        Categories
                      </span>
                    )}
                    <button
                      onClick={() => setIsSettingsCollapsed(!isSettingsCollapsed)}
                      className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      title={isSettingsCollapsed ? 'Expand categories' : 'Collapse categories'}
                    >
                      {isSettingsCollapsed ? (
                        <ChevronRight className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronLeft className="h-4 w-4 text-gray-500" />
                      )}
                    </button>
                  </div>
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                          activeTab === tab.id
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-l-4 border-blue-600'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 border-l-4 border-transparent'
                        } ${isSettingsCollapsed ? 'justify-center' : ''}`}
                        title={isSettingsCollapsed ? tab.label : undefined}
                      >
                        <Icon className="h-5 w-5 flex-shrink-0" />
                        {!isSettingsCollapsed && <span className="font-medium">{tab.label}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex-1">
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                  {loading ? (
                    <div className="flex items-center justify-center h-64">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    </div>
                  ) : activeTab === 'profile' && profile ? (
                    <ProfileSection
                      profile={profile}
                      userEmail={user?.email || ''}
                      saving={saving}
                      onProfileChange={setProfile}
                      onSave={saveProfile}
                    />
                  ) : activeTab === 'security' && user?.id ? (
                    <SecuritySection
                      userId={user.id}
                      saving={saving}
                      onSavingChange={setSaving}
                    />
                  ) : activeTab === 'notifications' && notificationSettings ? (
                    <div className="space-y-6">
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white">Notification Preferences</h2>

                      <div className="space-y-4">
                        {[
                          { key: 'email_notifications', label: 'Email Notifications', description: 'Receive email notifications' },
                          { key: 'bot_alerts', label: 'Bot Alerts', description: 'Get notified when bots start or stop' },
                          { key: 'trade_notifications', label: 'Trade Notifications', description: 'Notifications for each trade execution' },
                          { key: 'profit_alerts', label: 'Profit Alerts', description: 'Get notified when trades are profitable' },
                          { key: 'loss_alerts', label: 'Loss Alerts', description: 'Get notified when trades result in loss' },
                          { key: 'daily_summary', label: 'Daily Summary', description: 'Receive daily performance summary' },
                          { key: 'marketing_emails', label: 'Marketing Emails', description: 'Receive product updates and offers' }
                        ].map((setting) => (
                          <div key={setting.key} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <div className="flex-1">
                              <h3 className="font-medium text-gray-900 dark:text-white">{setting.label}</h3>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{setting.description}</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={notificationSettings[setting.key as keyof NotificationSettings] as boolean}
                                onChange={(e) => setNotificationSettings({
                                  ...notificationSettings,
                                  [setting.key]: e.target.checked
                                })}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-500 peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        ))}
                      </div>

                      <div className="flex justify-end">
                        <button
                          onClick={saveNotificationSettings}
                          disabled={saving}
                          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {saving ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Check className="h-4 w-4" />
                              Save Preferences
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ) : activeTab === 'subscription' && user?.id ? (
                    <SubscriptionSection
                      userId={user.id}
                      saving={saving}
                      onSavingChange={setSaving}
                    />
                  ) : activeTab === 'seller' && user?.id ? (
                    <SellerSection
                      userId={user.id}
                      saving={saving}
                      onSavingChange={setSaving}
                    />
                  ) : activeTab === 'contact' && user?.id ? (
                    <ContactSection
                      userId={user.id}
                      saving={saving}
                      onSavingChange={setSaving}
                    />
                  ) : null}
                </div>
              </div>
            </div>
      </motion.div>
    </DashboardLayout>
  );
};

export default SettingsPage;
